/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- File is too complex to refactor right now. Should be handled later */
import {
  type PaywallPreviewCouponApplication,
  useCouponPreviewMutation,
} from "@circle-react/components/Paywalls/Checkout/hooks/useCouponPreviewMutation";
import {
  CouponApplicationStatus,
  findPrice,
  getCouponApplication,
} from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { usePreviewMutation } from "@circle-react/components/Paywalls/Checkout/hooks/usePreviewMutation";
import { isActive } from "@circle-react/helpers/paywallHelpers";
import type {
  PaywallCheckoutPreview,
  PaywallFailure,
} from "@circle-react/types";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import type { UseMutationResult } from "react-query";

import { DEFAULT_ERROR } from "@/react/components/Paywalls/Checkout/helpers";
import { getPreviewParameters } from "@/react/components/Paywalls/Checkout/helpers";
import { useCheckoutSelectedPrice } from "@/react/components/Paywalls/Checkout/hooks/useCheckoutSelectedPrice";
import type { CheckoutFormValuesProps } from "@/react/components/Paywalls/Checkout/types/CheckoutFormValuesProps";
import type {
  CheckoutProps,
  PaywallCheckout,
} from "@/react/components/Paywalls/Checkout/types/CheckoutProps";
import { isValidTaxIdValue } from "@/react/utils/paywalls/taxId/helpers";

import { useCardInfo } from "./useCardInfo";
import { useDebouncedBillingAddress } from "./useDebouncedBillingAddress";
import { useMemberAddressAttributes } from "./useMemberAddressAttributes";

export interface PaymentError {
  disablePayButton: boolean;
  existingAccount?: boolean;
  message: string;
  type: string;
}

const initialPaymentError: PaymentError = {
  disablePayButton: false,
  existingAccount: false,
  message: "",
  type: "",
};

export interface PaywallHandlerError extends Error {
  body: {
    failure: {
      category: string;
      code: string;
      level: string;
      message: string;
    };
  };
}

export interface PreviewError extends Error {
  body: {
    message: string;
    error_details?: {
      category: string;
      code: string;
    };
  };
}

export interface UsePaywallCheckoutProps {
  checkoutPreviewContext?: CheckoutProps["checkoutPreviewContext"];
  isMemberAddressRequired: boolean;
  isMemberWithSavedPaymentMethods: boolean;
  isTaxIdMandatory: boolean;
  loginUrl: string;
  paywall: PaywallCheckout;
  resetPasswordUrl: string;
}

export type PaywallCheckoutPreviewMutationResult =
  | UseMutationResult<PaywallCheckoutPreview, PreviewError, any>
  | Record<string, never>;

export interface UsePaywallCheckoutReturn {
  amountDue: number;
  applyCouponCode: (props: { data?: PaywallPreviewCouponApplication }) => void;
  clearAlerts: () => void;

  clearCouponCode: (prop: { resetPreview: boolean }) => void;
  couponPreviewMutation: ReturnType<typeof useCouponPreviewMutation>;
  enableTaxIdCollection: () => void;
  getSelectedPrice: () => PaywallPrice;
  handleError: (param: any) => void;
  isCardInfoComplete: boolean;
  isCardInfoRequired: boolean;
  isCardInfoValid: boolean;
  isMemberUsingSavedPaymentMethod: boolean;
  isPaywallActive: boolean;
  isPerformingVerification: boolean;
  isProcessingPayment: boolean;
  isSelectedPrice: (price: PaywallPrice) => boolean;
  isSetupIntentRequired: boolean;
  isTaxIdCollectionEnabled: boolean;
  loginRedirectUrl: string;
  paymentError: PaymentError;
  paywallFailure: PaywallFailure | null;
  previewMutation: PaywallCheckoutPreviewMutationResult;
  refreshCheckoutPreview: () => Promise<void>;
  resetCheckoutPreviewError: (error: unknown) => void;
  resetPasswordRedirectUrl: string;
  setIsCardInfoComplete: (arg: boolean) => void;
  setIsMemberUsingSavedPaymentMethod: (arg: boolean) => void;
  setIsPerformingVerification: (arg: boolean) => void;
  setIsProcessingPayment: (arg: boolean) => void;
  setMemberAddressAttributes: (event: any) => void;
  setPaymentError: Dispatch<SetStateAction<PaymentError>>;
  setPaywallFailure: Dispatch<SetStateAction<PaywallFailure | null>>;
  setSelectedPrice: (priceId: number) => void;

  // TODO: replace these props with a hook similar to `useCheckoutSelectedPrice` that relies on the `payment_method_type` form value.
  // We are using `stripePaymentMethodType` to set the `payment_method_type` prop when submitting
  // requests to our backend. This is unnecessary as we can derive the payment method type from the
  // form value.
  setStripePaymentMethodType: (arg: string) => void;
  stripePaymentMethodType: string;
}

export const usePaywallCheckout = ({
  isMemberAddressRequired,
  isTaxIdMandatory,
  paywall,
  isMemberWithSavedPaymentMethods,
  loginUrl,
  resetPasswordUrl,
  checkoutPreviewContext,
}: UsePaywallCheckoutProps): UsePaywallCheckoutReturn => {
  const { watch, setValue, setError, getValues, clearErrors } =
    useFormContext<CheckoutFormValuesProps>();

  const navigate = useNavigate();

  const [isMemberUsingSavedPaymentMethod, setIsMemberUsingSavedPaymentMethod] =
    useState(false);

  const [stripePaymentMethodType, setStripePaymentMethodType] =
    useState("card");
  const [isCardInfoComplete, setIsCardInfoComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(initialPaymentError);
  const [paywallFailure, setPaywallFailure] = useState<PaywallFailure | null>(
    null,
  );
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [amountDue, setAmountDue] = useState(0);
  const [isFreeForever, setIsFreeForever] = useState(false);

  const clearAlerts = () => {
    setPaymentError(initialPaymentError);
    setPaywallFailure(null);
  };

  useEffect(() => {
    if (paymentError.existingAccount) {
      setError("email", { type: "duplicated" });
    }
  }, [setError, paymentError]);

  const couponPreviewMutation = useCouponPreviewMutation({
    checkoutPreviewContext,
    paywall,
  });
  const previewMutation = usePreviewMutation({
    checkoutPreviewContext,
    paywall,
  });

  const {
    paywall_price_id,
    payment_method_type,
    community_member_billing_info_attributes,
  } = watch();

  const previewCheckout = useCallback(
    (couponPreviewData?: PaywallPreviewCouponApplication) => {
      const params: any = getPreviewParameters(getValues());

      const price = findPrice(
        paywall_price_id,
        couponPreviewData?.paywall?.prices ||
          couponPreviewMutation?.data?.paywall?.prices,
      );
      const couponApplication = getCouponApplication(
        price?.coupon_applications,
      );

      if (couponApplication.status !== CouponApplicationStatus.COUPON_APPLIED) {
        params.queryParams.coupon_code = "";
      }

      previewMutation.mutate(params, {
        onSuccess: preview => {
          setPaymentError(initialPaymentError);
          setAmountDue(preview.amount_due_now);
          setIsFreeForever(preview.free_forever);
        },
        onError: error => {
          console.error(error);

          if (error.body.error_details?.code?.match(/coupon_/)) {
            previewCheckout();
            return;
          }

          setPaymentError({
            message: error.message,
            type: "danger",
            disablePayButton: true,
          });
        },
      });
    },
    [
      setValue,
      setPaymentError,
      previewMutation.mutate,
      couponPreviewMutation?.data,
      getValues,
      paywall_price_id,
    ],
  );

  const refreshCheckoutPreview = useCallback(async () => {
    const params: any = getPreviewParameters(getValues());
    const price = findPrice(
      paywall_price_id,
      couponPreviewMutation?.data?.paywall?.prices,
    );
    const couponApplication = getCouponApplication(price?.coupon_applications);

    if (couponApplication.status !== CouponApplicationStatus.COUPON_APPLIED) {
      params.queryParams.coupon_code = "";
    }

    const fetchPreview = async () => {
      const preview = await previewMutation.mutateAsync(params);
      setPaymentError(initialPaymentError);
      setAmountDue(preview.amount_due_now);
      setIsFreeForever(preview.free_forever);
    };
    const handleRefreshError = (error: PreviewError) => {
      console.error(error);
      setPaymentError({
        message: error.message,
        type: "danger",
        disablePayButton: true,
      });
    };

    previewMutation.reset();
    try {
      await fetchPreview();
    } catch (error: unknown) {
      const previewError = error as PreviewError;
      if (previewError.body?.error_details?.code?.match(/coupon_/)) {
        setValue("coupon_code", "");
        couponPreviewMutation.reset();
        params.queryParams.coupon_code = "";
        try {
          await fetchPreview();
          return;
        } catch (retryError: unknown) {
          handleRefreshError(retryError as PreviewError);
          throw retryError;
        }
      }

      handleRefreshError(previewError);
      throw error;
    }
  }, [
    couponPreviewMutation.data,
    couponPreviewMutation.reset,
    getValues,
    paywall_price_id,
    previewMutation.mutateAsync,
    previewMutation.reset,
    setPaymentError,
    setValue,
  ]);

  const resetCheckoutPreviewError = useCallback(
    (error: unknown) => {
      previewMutation.reset();
      setPaymentError(currentError =>
        error instanceof Error &&
        currentError.disablePayButton &&
        currentError.type === "danger" &&
        currentError.message === error.message
          ? initialPaymentError
          : currentError,
      );
    },
    [previewMutation.reset, setPaymentError],
  );

  const debouncedBillingAddress = useDebouncedBillingAddress(
    community_member_billing_info_attributes,
  );

  // previewCheckout is intentionally omitted from the deps: its identity changes when
  // previewMutation changes mid-flight, which would cause an infinite loop.
  // getValues() inside always reads fresh form values via a ref.

  useEffect(() => {
    previewCheckout();
  }, [paywall_price_id, payment_method_type, debouncedBillingAddress]);

  const { getSelectedPrice, setSelectedPrice, isSelectedPrice } =
    useCheckoutSelectedPrice(paywall, previewCheckout);

  const setMemberAddressAttributes = useMemberAddressAttributes(
    watch,
    setValue,
    isMemberAddressRequired,
  );

  const applyCouponCode = useCallback(
    ({ data }: { data?: PaywallPreviewCouponApplication }) => {
      const code = data?.paywall_coupon?.code || watch().coupon_code || "";
      setValue("coupon_code", code.toUpperCase());

      previewCheckout(data);
      if (!checkoutPreviewContext) {
        void navigate({
          to: ".",
          search: prev =>
            ({
              ...prev,
              coupon_code: code,
            }) as unknown as typeof prev,
          replace: true,
        });
      }
    },
    [watch, setValue, previewCheckout, checkoutPreviewContext, navigate],
  );

  const clearCouponCode = useCallback(
    ({ resetPreview = true }) => {
      clearErrors("coupon_code");

      setValue("coupon_code", "");
      setIsFreeForever(false);

      if (resetPreview) {
        couponPreviewMutation.reset();
        previewCheckout();
      }

      void navigate({
        to: ".",
        search: prev => ({
          ...prev,
          coupon_code: undefined,
        }),
        replace: true,
      });
    },
    [setValue, previewCheckout, clearErrors, couponPreviewMutation, navigate],
  );

  const [isTaxIdCollectionEnabled, setTaxIdCollectionEnabled] =
    useState(isTaxIdMandatory);

  const enableTaxIdCollection = useCallback(
    () => setTaxIdCollectionEnabled(true),
    [setTaxIdCollectionEnabled],
  );

  const [taxIdDebounceTimer, setTaxIdDebounceTimer] = useState<any>();
  const clearTaxIdDebounceTimer = useCallback(() => {
    if (taxIdDebounceTimer) {
      clearTimeout(taxIdDebounceTimer);
      setTaxIdDebounceTimer(undefined);
    }
  }, [taxIdDebounceTimer, setTaxIdDebounceTimer]);
  useEffect(() => {
    const debouncePreviewCheckout = () => {
      clearTaxIdDebounceTimer();
      setTaxIdDebounceTimer(
        setTimeout(() => {
          previewCheckout();
        }, 250),
      );
    };

    const taxIdChangesSub = watch((values, { name }) => {
      if (name === "community_member_billing_info_attributes.tax_id_type") {
        const taxIdEnum =
          values["community_member_billing_info_attributes"]?.["tax_id_type"] ||
          "";

        if (taxIdEnum === "") {
          debouncePreviewCheckout();
        }
      } else if (
        name === "community_member_billing_info_attributes.tax_id_value"
      ) {
        const taxIdEnum =
          values["community_member_billing_info_attributes"]?.["tax_id_type"] ||
          "";
        const taxIdValue =
          values["community_member_billing_info_attributes"]?.[
            "tax_id_value"
          ] || "";

        if (
          isValidTaxIdValue({
            taxIdEnum,
            taxIdValue,
            countryCode:
              community_member_billing_info_attributes?.address_country,
          })
        ) {
          debouncePreviewCheckout();
        }
      }
    });

    return () => {
      taxIdChangesSub.unsubscribe();
      clearTaxIdDebounceTimer();
    };
  }, [
    watch,
    previewCheckout,
    clearTaxIdDebounceTimer,
    setTaxIdDebounceTimer,
    clearAlerts,
  ]);

  const selectedPrice = getSelectedPrice();
  const isPaywallActive = isActive(paywall);

  const { isCardInfoRequired, isCardInfoValid, isSetupIntentRequired } =
    useCardInfo(
      paywall,
      selectedPrice,
      payment_method_type,
      isCardInfoComplete,
      isMemberWithSavedPaymentMethods,
      isMemberUsingSavedPaymentMethod,
      isFreeForever,
    );

  const handleError = (options: any) => {
    setPaymentError({ ...DEFAULT_ERROR, ...options });
  };

  const loginRedirectUrl = useMemo(() => {
    const hasEmailLoginOption = loginUrl.includes("#email");
    const baseUrl = loginUrl.replace("#email", "");
    const redirectUrl = `${baseUrl}?post_login_redirect=${encodeURIComponent(window.location.href)}`;
    return hasEmailLoginOption ? `${redirectUrl}#email` : redirectUrl;
  }, [loginUrl, window.location.href]);

  const resetPasswordRedirectUrl = useMemo(
    () =>
      `${resetPasswordUrl}?post_login_redirect=${encodeURIComponent(
        window.location.href,
      )}`,
    [resetPasswordUrl, window.location.href],
  );

  const [isPerformingVerification, setIsPerformingVerification] =
    useState<boolean>(false);

  return {
    clearAlerts,
    setSelectedPrice,
    getSelectedPrice,
    isSelectedPrice,
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    isTaxIdCollectionEnabled,
    paymentError,
    couponPreviewMutation,
    previewMutation,
    // previewCheckout identity may be stale; react-query's mutate ref is stable in practice
    refreshCheckoutPreview,
    resetCheckoutPreviewError,
    setMemberAddressAttributes,
    setPaymentError,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    amountDue,
    stripePaymentMethodType,
    setStripePaymentMethodType,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    paywallFailure,
    setPaywallFailure,
    loginRedirectUrl,
    resetPasswordRedirectUrl,
    isPerformingVerification,
    setIsPerformingVerification,
  };
};
